// --- React & Redux
import React, { useEffect, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import { useNavigate, useLocation } from 'react-router-dom';
// --- External UI Libraries
import Input from '@dtci/spellbook/Input';
import Button from '@dtci/spellbook/Button';
import { Col, Row } from 'antd';
import Select from '@dtci/spellbook/Select';
// --- Components
import { InputLabel } from 'datastage/components/InputLabel';
// --- Styles
import './styles.scss';
// --- API & Utils
import { datastageApi } from 'datastage/api';
import { getItemRange } from 'datastage/utils/pagination';
import { parseSearchParams, updateSearchParams } from 'datastage/utils/urlParams';
import { findRoleByValue, cleanUserFilter } from './utils/usersSearchUtils';
import { validateSpecialCharacters, validateEmail } from 'datastage/utils/validateSpecialCharacters';
// --- Custom Hooks
import { useUsersActions } from 'datastage/store/users/useUsers';
import { useToasts } from 'datastage/utils/hooks/useToasts';
import { useUsersData } from '../../hooks/useUsersData';

/**
 * Search form for filtering the Users grid.
 *
 * Available filters:
 * - Name: text search on user's name (with special character validation)
 * - Email: text search on user's email (with email format validation)
 * - Role: single-select dropdown for user role
 *
 * Filter state syncs bidirectionally with URL params for shareable searches.
 * Changes trigger debounced searches (400ms delay) for better UX.
 *
 * @component
 * @returns {JSX.Element} The rendered user search form.
 */
export function UsersSearchForm() {
    const navigate = useNavigate();
    const location = useLocation();
    const isInitialMount = useRef(true);
    const { fetchUsers, updateUsers, userRange, filterForm, userSort, itemsPerPage } =
        useUsersData();
    const { updateUsersSearchForm, clearUsers } = useUsersActions();
    const { roleOptions } = useSelector((s) => s.users);
    const { showErrorToast } = useToasts();

    // Validation state for inline error messages
    const [validationErrors, setValidationErrors] = useState({
        name: undefined,
        email: undefined,
    });
    const debounceTimerRef = useRef(null);

    // Fetch role options on mount if not already loaded
    useEffect(() => {
        if (!Array.isArray(roleOptions) || roleOptions.length === 0) {
            (async () => {
                try {
                    const roles = await datastageApi.userRoles();
                    updateUsers({ roleOptions: roles });
                } catch (error) {
                    showErrorToast('Failed to load role options');
                    console.error('Error fetching user roles:', error);
                }
            })();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // Sync URL params to form state on navigation (location.key changes on each navigation)
    useEffect(() => {
        const params = parseSearchParams(location.search);
        const filterData = buildFilterFromParams(params, roleOptions);

        // Handle pageSize from URL (TAP pattern)
        let rangeOverride;
        if (params.pageSize) {
            const newPageSize = parseInt(params.pageSize, 10);
            if (!isNaN(newPageSize) && newPageSize > 0) {
                rangeOverride = getItemRange(0, newPageSize);
                updateUsers({ itemsPerPage: newPageSize, userRange: rangeOverride, currentPage: 0 });
            }
        }

        const hasUrlParams = Object.keys(filterData).length > 0;

        if (hasUrlParams) {
            updateUsersSearchForm({ key: 'filterForm', data: filterData });
        }

        // Merge URL-derived filters onto Redux filterForm so defaults apply when the URL
        // only carries partial params (e.g. pageSize).
        const filterForFetch = hasUrlParams ? { ...filterForm, ...filterData } : filterForm;

        fetchData(filterForFetch, rangeOverride);
        isInitialMount.current = false;
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [location.key]);

    // Re-parse URL params when roleOptions load (select value depends on options)
    useEffect(() => {
        if (isInitialMount.current || !Array.isArray(roleOptions) || roleOptions.length === 0) {
            return;
        }

        const params = parseSearchParams(location.search);
        if (params.role) {
            const filterData = buildFilterFromParams(params, roleOptions);
            const mergedFilterData = { ...filterForm, ...filterData };
            updateUsersSearchForm({ key: 'filterForm', data: filterData });

            // Handle pageSize if present
            let rangeOverride;
            if (params.pageSize) {
                const newPageSize = parseInt(params.pageSize, 10);
                if (!isNaN(newPageSize) && newPageSize > 0) {
                    rangeOverride = getItemRange(0, newPageSize);
                }
            }

            fetchData(mergedFilterData, rangeOverride);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [roleOptions]);

    /**
     * Builds filter form data from URL search params.
     * Converts role id from the URL to a single role option for Select.
     */
    function buildFilterFromParams(params, options) {
        const filterData = {};

        if (params.name) filterData.name = params.name;
        if (params.email) filterData.email = params.email;

        if (params.role && Array.isArray(options) && options.length > 0) {
            const roleValues = Array.isArray(params.role) ? params.role : [params.role];
            const first = String(roleValues[0] ?? '')
                .split(',')
                .map((s) => s.trim())
                .find((s) => s !== '');
            const matched = first != null ? findRoleByValue(options, first) : null;
            if (matched) {
                filterData.role = matched;
            }
        }

        return filterData;
    }

    /**
     * Fetches user data and updates Redux state.
     * @param {Object} filterData - Filter criteria
     * @param {Array} rangeOverride - Optional range override (e.g., when pageSize changes)
     */
    const fetchData = async (filterData, rangeOverride) => {
        const range = rangeOverride ?? getItemRange(0, itemsPerPage);
        const data = await fetchUsers(range, filterData);
        if (data) {
            updateUsers({
                users: data?.content ?? [],
                totalPages: data?.totalPages ?? 0,
                currentPage: data?.pageable?.pageNumber ?? 0,
                totalItems: data?.totalElements ?? 0,
                userRange: range,
            });
        }
    };

    /**
     * Reset navigates to clean URL; the location.key useEffect handles re-fetch.
     */
    const handleResetFilter = () => {
        clearUsers();
        setValidationErrors({
            name: undefined,
            email: undefined,
        });
        navigate(location.pathname);
    };

    /**
     * Handles input changes with validation and debounced search.
     * Validates name for special characters and email for format.
     * Triggers search after 400ms of no typing.
     */
    function handleInputChange(field, value) {
        let newValidationErrors = validationErrors;

        // Validate based on field type
        if (field === 'name') {
            const error = validateSpecialCharacters(value);
            newValidationErrors = { ...validationErrors, [field]: error };
            setValidationErrors(newValidationErrors);
        } else if (field === 'email') {
            const error = validateEmail(value);
            newValidationErrors = { ...validationErrors, [field]: error };
            setValidationErrors(newValidationErrors);
        }

        // Update form state
        const newFilterForm = { ...filterForm, [field]: value };
        updateUsersSearchForm({
            key: 'filterForm',
            data: { [field]: value },
        });

        // Debounce the search to avoid excessive API calls
        if (debounceTimerRef.current) {
            clearTimeout(debounceTimerRef.current);
        }
        debounceTimerRef.current = setTimeout(() => {
            const hasErrors = Object.values(newValidationErrors).some((error) => error);
            if (!hasErrors) {
                const firstPageRange = getItemRange(0, itemsPerPage);
                updateUsers({
                    userRange: firstPageRange,
                    currentPage: 0,
                });
                const filtered = cleanUserFilter(newFilterForm, firstPageRange, userSort);
                updateSearchParams(location, navigate, filtered);
            }
        }, 400);
    }

    return (
        <Row className="users-filter-form">
            <Col>
                <InputLabel colProps={{ span: 24 }} label="Name">
                    <Input
                        value={filterForm.name}
                        onChange={(e) => handleInputChange('name', e.target.value)}
                        width="auto"
                        id="name"
                        name="name"
                        placeholder="Enter"
                        className={validationErrors.name ? 'input-error' : ''}
                    />
                    {validationErrors.name && (
                        <div className="validation-error">{validationErrors.name}</div>
                    )}
                </InputLabel>
            </Col>
            <Col>
                <InputLabel colProps={{ span: 24 }} label="Email">
                    <Input
                        value={filterForm.email}
                        onChange={(e) => handleInputChange('email', e.target.value)}
                        width="auto"
                        id="email"
                        name="email"
                        placeholder="Enter"
                        className={validationErrors.email ? 'input-error' : ''}
                    />
                    {validationErrors.email && (
                        <div className="validation-error">{validationErrors.email}</div>
                    )}
                </InputLabel>
            </Col>
            <Col>
                <InputLabel colProps={{ span: 24 }} label="Role">
                    <Select
                        options={roleOptions}
                        isClearable
                        isSearchable
                        onChange={(value) => handleInputChange('role', value)}
                        value={filterForm.role || null}
                    />
                </InputLabel>
            </Col>
            <Col>
                <Button
                    className="reset-button"
                    sizeType="small"
                    styleType="standard"
                    onClick={handleResetFilter}
                >
                    Reset
                </Button>
            </Col>
        </Row>
    );
}
